import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import WellnessSlider from './WellnessSlider';

const EditWellnessModal = ({ entry, onSave, onClose, isSaving }) => {
  const [formData, setFormData] = useState({
    date: '',
    sleep: 4,
    fatigue: 4,
    soreness: 3,
    stress: 3,
    notes: ''
  });

  useEffect(() => {
    if (entry) {
      setFormData({
        date: entry?.date || '',
        sleep: entry?.sleep || 4,
        fatigue: entry?.fatigue || 4,
        soreness: entry?.soreness || 3,
        stress: entry?.stress || 3,
        notes: entry?.notes || ''
      });
    }
  }, [entry]);

  const handleWellnessChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const calculateHooperIndex = () => {
    return formData?.sleep + formData?.fatigue + formData?.soreness + formData?.stress;
  };

  const getHooperStatus = (score) => {
    if (score <= 12) return { label: 'Excellent', color: 'text-success' };
    if (score <= 16) return { label: 'Good', color: 'text-info' };
    if (score <= 20) return { label: 'Fair', color: 'text-warning' };
    return { label: 'Poor', color: 'text-error' };
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSave({
      ...entry,
      ...formData
    });
  };

  const hooperIndex = calculateHooperIndex();
  const hooperStatus = getHooperStatus(hooperIndex);

  const wellnessSliderConfigs = [
    {
      key: 'sleep',
      label: 'Sleep Quality',
      icon: 'Moon',
      description: 'How well did you sleep?',
      anchors: ['Very Poor', 'Average', 'Excellent']
    },
    {
      key: 'fatigue',
      label: 'Fatigue Levels',
      icon: 'Battery',
      description: 'How tired did you feel?',
      anchors: ['Very Fresh', 'Moderate', 'Exhausted']
    },
    {
      key: 'soreness',
      label: 'Muscle Soreness',
      icon: 'Zap',
      description: 'How sore were your muscles?',
      anchors: ['No Soreness', 'Some Soreness', 'Very Sore']
    },
    {
      key: 'stress',
      label: 'Stress Levels',
      icon: 'Brain',
      description: 'How stressed did you feel?',
      anchors: ['Very Relaxed', 'Moderate', 'Very Stressed']
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-text-primary mb-1">Edit Wellness Entry</h2>
            <p className="text-sm text-text-secondary">
              {new Date(formData?.date)?.toLocaleDateString('en-GB', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary smooth-transition"
            disabled={isSaving}
          >
            <Icon name="X" size={24} strokeWidth={2} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Date Field */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Date
            </label>
            <Input
              type="date"
              value={formData?.date}
              onChange={(e) => setFormData(prev => ({ ...prev, date: e?.target?.value }))}
              required
            />
          </div>

          {/* Hooper Index Preview */}
          <div className="mb-6 p-4 bg-muted rounded-lg border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-text-secondary mb-1">Current Hooper Index</p>
                <div className="flex items-center space-x-3">
                  <span className="text-3xl font-bold text-text-primary">{hooperIndex}</span>
                  <span className={`text-sm font-medium ${hooperStatus?.color}`}>
                    {hooperStatus?.label}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-text-secondary mb-1">Lower scores = Better wellness</p>
                <p className="text-xs text-text-secondary">Range: 4-28</p>
              </div>
            </div>
          </div>

          {/* Wellness Sliders */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {wellnessSliderConfigs?.map((config) => (
              <WellnessSlider
                key={config?.key}
                label={config?.label}
                value={formData?.[config?.key]}
                onChange={(value) => handleWellnessChange(config?.key, value)}
                icon={config?.icon}
                description={config?.description}
                anchors={config?.anchors}
              />
            ))}
          </div>

          {/* Notes Field */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-text-primary mb-2">
              <div className="flex items-center space-x-2">
                <Icon name="FileText" size={16} strokeWidth={2} />
                <span>Notes</span>
              </div>
            </label>
            <textarea
              value={formData?.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e?.target?.value }))}
              placeholder="Add any additional notes about your wellness..."
              rows={3}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent smooth-transition resize-none"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isSaving}
              iconName="Save"
              iconPosition="left"
            >
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditWellnessModal;